import type { PropsWithChildren, FunctionComponent } from 'react'
import { FormattedMessage, injectIntl } from 'react-intl'
import { spacing, TextField } from '@retailer-platform/shared-components/src/tds'
import { Checkbox } from 'ic-snacks'
import { type InjectedDashIntlProps } from '../../../../../../intl/intl.types'
import Card from '../../../../../components/Card/Card'
import { type StoreGroupsFormikProps } from '../utils/form.types'
import useAccessControl from '../../../../../components/AccessControl/useAccessControl'
import { FeatureEntitlements } from '../../../../../common/types'
import styled from '@emotion/styled'

interface Props extends StoreGroupsFormikProps, InjectedDashIntlProps {}

const CardBody = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: spacing.s24,
})

export const _StoreGroupsFormNameField: FunctionComponent<PropsWithChildren<Props>> = ({
  values: { name },
  errors: { name: nameError },
  intl,
  setValues,
  values,
}) => {
  const placeholder = intl.formatMessage({
    id: 'settings.storeGroups.form.labels.name',
  })

  const caperCheckboxLabel = intl.formatMessage({
    id: 'settings.storeGroups.form.labels.caperPowered',
  })

  const hasAccess = useAccessControl()
  const hasCaperEntitlement = hasAccess({
    featureEntitlementIds: [FeatureEntitlements.Caper],
  })

  const CAPER_PREFIX = '[Caper] '
  const isCaperGroup = name.startsWith(CAPER_PREFIX)

  const handleCaperCheckboxChange = () => {
    if (isCaperGroup) {
      // Remove the [Caper] prefix
      setValues({ ...values, name: name.slice(CAPER_PREFIX.length) })
    } else {
      // Add the [Caper] prefix
      setValues({ ...values, name: CAPER_PREFIX + name })
    }
  }

  return (
    <Card margin="0 0 20px 0">
      <Card.Header title={<FormattedMessage id="settings.storeGroups.form.groupDetail" />} />
      <Card.Body>
        <CardBody>
          <TextField
            name="name"
            value={name}
            onChange={e => setValues({ ...values, name: e.target.value })}
            description={nameError}
            error={!!nameError}
            placeholder={placeholder}
            data-testid="store-group-name-field"
            labelProps={{
              label: placeholder,
            }}
          />
          {hasCaperEntitlement && (
            <Checkbox
              id="caper-store-group-checkbox"
              isSelected={isCaperGroup}
              onChange={handleCaperCheckboxChange}
              data-testid="caper-store-group-checkbox"
            >
              {caperCheckboxLabel}
            </Checkbox>
          )}
        </CardBody>
      </Card.Body>
    </Card>
  )
}

export const StoreGroupsFormNameField = injectIntl(_StoreGroupsFormNameField)
